import type { AgentRunSummary } from "@common/schemas/domains/agentRuns/agent-run.schema";
import type {
    DemoDataset,
    DemoRunRecord,
    DemoViewerRecord,
} from "./busyClosesDemo.types";
import { toDemoEvent } from "./buildDemoRun";

const RUN_EVENT_COOLDOWN_MS = 2 * 60_000;
const MAX_VIEWERS = 3;

interface DemoStoreState {
    runsById: ReadonlyMap<string, DemoRunRecord>;
    viewersByTargetId: ReadonlyMap<string, DemoViewerRecord>;
}

let state: DemoStoreState = {
    runsById: new Map(),
    viewersByTargetId: new Map(),
};

const listeners = new Set<() => void>();

const setState = (next: DemoStoreState) => {
    state = next;
    listeners.forEach((listener) => listener());
};

export const subscribeBusyClosesDemoStore = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

export const getBusyClosesDemoState = (): DemoStoreState => state;

const mountCountsById = new Map<string, number>();
const isMounted = (id: string) => (mountCountsById.get(id) ?? 0) > 0;
const addMountCount = ({ ids, delta }: { ids: string[]; delta: number }) =>
    ids.forEach((id) =>
        mountCountsById.set(id, (mountCountsById.get(id) ?? 0) + delta),
    );

/** Adds a dataset's runs and viewers the store doesn't hold yet; live ones keep their history. */
const addNewRecords = (dataset: DemoDataset) => {
    const newRuns = dataset.runs.filter(
        (record) => !state.runsById.has(record.run.id),
    );
    const newViewers = [...dataset.viewersByTargetId].filter(
        ([targetId]) => !state.viewersByTargetId.has(targetId),
    );
    if (newRuns.length === 0 && newViewers.length === 0) {
        return;
    }
    setState({
        runsById: new Map([
            ...state.runsById,
            ...newRuns.map((record): [string, DemoRunRecord] => [
                record.run.id,
                record,
            ]),
        ]),
        viewersByTargetId: new Map([...state.viewersByTargetId, ...newViewers]),
    });
};

/** Mounts a dataset; its runs and viewers tick until the returned unmount, then keep their history. */
export const registerBusyClosesDemoDataset = (dataset: DemoDataset) => {
    const ids = [
        ...dataset.runs.map((record) => record.run.id),
        ...dataset.viewersByTargetId.keys(),
    ];
    addMountCount({ ids, delta: 1 });
    addNewRecords(dataset);
    return () => addMountCount({ ids, delta: -1 });
};

const withAppendedEvent = ({
    record,
    now,
}: {
    record: DemoRunRecord;
    now: number;
}): DemoRunRecord => {
    const [step, ...queue] = record.queue;
    const at = new Date(now).toISOString();
    const run: AgentRunSummary = {
        ...record.run,
        pausedAt: step.shouldResume ? null : record.run.pausedAt,
        progress: {
            ...step.progress,
            at,
            segment: (record.run.progress?.segment ?? 0) + 1,
        },
        counters: {
            ...record.run.counters,
            stepCount: record.run.counters.stepCount + 3,
            toolInvocationCount: record.run.counters.toolInvocationCount + 2,
        },
        updatedAt: at,
    };
    const event = toDemoEvent({
        id: step.eventId,
        run,
        payload: step.payload,
        actorId: step.actorId,
        createdAt: now,
    });
    return {
        ...record,
        run,
        events: [event, ...record.events],
        queue,
        lastEventAt: now,
    };
};

/** Appends the next scripted event to one mounted run that has one queued and hasn't moved in two minutes. */
export const appendNextBusyClosesDemoRunEvent = ({
    now,
    random,
}: {
    now: number;
    random: () => number;
}) => {
    const eligibleRecords = [...state.runsById.values()].filter(
        (record) =>
            isMounted(record.run.id) &&
            record.queue.length > 0 &&
            now - record.lastEventAt >= RUN_EVENT_COOLDOWN_MS,
    );
    if (eligibleRecords.length === 0) {
        return;
    }
    const record =
        eligibleRecords[Math.floor(random() * eligibleRecords.length)];
    setState({
        ...state,
        runsById: new Map([
            ...state.runsById,
            [record.run.id, withAppendedEvent({ record, now })],
        ]),
    });
};

const getNextViewerIds = ({
    record,
    random,
}: {
    record: DemoViewerRecord;
    random: () => number;
}): number[] => {
    const absentIds = record.candidateIds.filter(
        (id) => !record.viewerIds.includes(id),
    );
    const canJoin =
        absentIds.length > 0 && record.viewerIds.length < MAX_VIEWERS;
    const shouldJoin =
        canJoin && (record.viewerIds.length === 0 || random() < 0.5);
    if (shouldJoin) {
        return [
            ...record.viewerIds,
            absentIds[Math.floor(random() * absentIds.length)],
        ];
    }
    const leavingIndex = Math.floor(random() * record.viewerIds.length);
    return record.viewerIds.filter((_, index) => index !== leavingIndex);
};

/** One teammate joins or leaves one mounted fake item, keeping it at zero to three viewers. */
export const shiftBusyClosesDemoViewers = ({
    random,
}: {
    random: () => number;
}) => {
    const targets = [...state.viewersByTargetId].filter(
        ([targetId, record]) =>
            isMounted(targetId) && record.candidateIds.length > 0,
    );
    if (targets.length === 0) {
        return;
    }
    const [targetId, record] = targets[Math.floor(random() * targets.length)];
    setState({
        ...state,
        viewersByTargetId: new Map([
            ...state.viewersByTargetId,
            [
                targetId,
                { ...record, viewerIds: getNextViewerIds({ record, random }) },
            ],
        ]),
    });
};
